/**
 * Color Palette Generation
 *
 * Generates color palettes with light/dark mode variants from a single hex color.
 * Used by ThemeProvider to create custom themes from URL query params or page config.
 *
 * ## How it works
 *
 * 1. Takes an input hex color (e.g., #dd3333)
 * 2. Generates a full palette of shades (100-950)
 * 3. For dark mode, inverts the shade scale around 600 for proper contrast
 *
 * ## Crossover Pattern (matching colors.ts)
 *
 * In dark mode, lighter shades should appear darker and vice versa,
 * with shade 600 as the pivot point (same for both modes):
 * - Light mode: 100=lightest, 950=darkest, 600=base
 * - Dark mode: 100=darkest, 950=lightest, 600=base (same as light)
 *
 * This maintains proper contrast on dark backgrounds while keeping
 * the primary brand color (600) consistent across modes.
 */
import tinycolor from "tinycolor2";
import { ColorPalette } from "@shared/theme/types";

const BASE_SHADE_LEVEL = 600;

/**
 * Maps shades to their inverted counterparts for dark mode.
 * Shade 600 (the base color) stays the same - it's the pivot point.
 * Shades below 600 map to shades above 600 and vice versa.
 */
const DARK_MODE_SHADE_MAP: Record<number, number> = {
  100: 950,
  200: 900,
  300: 800,
  400: 700,
  500: 500, // Unique intermediate value
  600: 600, // Pivot - same for both modes
  700: 500,
  800: 400,
  900: 300,
  950: 200,
};

/**
 * Alpha values for blending each shade with white (lighter) or black (darker).
 * Shade 600 is the base (alpha = 1), shades below blend with white,
 * shades above blend with black.
 */
const BLEND_MAP: Record<number, number> = {
  100: 0.15,
  200: 0.3,
  300: 0.45,
  400: 0.6,
  500: 0.8,
  600: 1, // Base color - no blending
  700: 0.8,
  800: 0.6,
  900: 0.4,
  950: 0.25,
};

/**
 * Blend a foreground color with a background color at a given alpha.
 */
function blendWithBackground(
  fg: tinycolor.Instance,
  bg: tinycolor.Instance,
  alpha: number
): string {
  const fgRgb = fg.toRgb();
  const bgRgb = bg.toRgb();

  const r = Math.round(fgRgb.r * alpha + bgRgb.r * (1 - alpha));
  const g = Math.round(fgRgb.g * alpha + bgRgb.g * (1 - alpha));
  const b = Math.round(fgRgb.b * alpha + bgRgb.b * (1 - alpha));

  return tinycolor({ r, g, b }).toHexString();
}

/**
 * Generate a single shade from the base color.
 * Shades < 600 blend with white, shades > 600 blend with black.
 */
function generateShade(baseHex: string, shadeLevel: number): string {
  const base = tinycolor(baseHex);
  const isLighter = shadeLevel < BASE_SHADE_LEVEL;
  const bg = tinycolor(isLighter ? "#ffffff" : "#000000");
  const alpha = BLEND_MAP[shadeLevel] ?? 1;

  return blendWithBackground(base, bg, alpha);
}

/**
 * Generate a full color palette with light/dark mode variants.
 *
 * @param hex - The base color (will be placed at shade 600)
 * @returns ColorPalette with { shade: { light, dark } } format
 *
 * @example
 * const palette = colorToPalette("#dd3333");
 * // palette[600].light === "#dd3333"
 * // palette[600].dark === "#dd3333" (same - pivot point)
 * // palette[100].light === very light red
 * // palette[100].dark === very dark red (inverted)
 */
export function colorToPalette(hex: string): ColorPalette {
  const shades = [100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

  return shades.reduce<ColorPalette>((palette, shade) => {
    const invertedShade = DARK_MODE_SHADE_MAP[shade];

    return {
      ...palette,
      [shade]: {
        light: shade === BASE_SHADE_LEVEL ? hex : generateShade(hex, shade),
        dark:
          invertedShade === BASE_SHADE_LEVEL
            ? hex
            : generateShade(hex, invertedShade),
      },
    };
  }, {});
}

/**
 * Validate and normalize a color string to hex format.
 * Returns null if invalid.
 */
export function validateColorString(str: unknown): string | null {
  if (str && typeof str === "string") {
    const color = tinycolor(str);
    if (color.isValid()) {
      return color.toHexString();
    }
  }
  return null;
}
